import { createContext, useContext, useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useUser } from './UserContext';
import { useProfilXp } from '../hooks/fetch_api_hook/useProfilXp';
import { usePostApi } from '../hooks/post_api_hook/usePostApi';
import { url } from '../utils/confserveur';
import { progressionRatio } from '../utils/xpConfig';

/**
 * Contexte XP élève : profil (XP/niveau/étoiles/flamme/skins), refresh, équipement de skin.
 * Le back est la source de vérité ; le front lit GET /profils-xp/me/ et affiche.
 *
 * À monter UNIQUEMENT autour de l'espace élève (XpProvider dans RootEleve) → réservé aux élèves,
 * pas de requête (donc pas de 403) pour les autres rôles.
 */
const XpContext = createContext(null);

export const XpProvider = ({ children }) => {
  const { user, isAuthenticated } = useUser();
  const isEleve = isAuthenticated && user?.role === 'eleve';

  // Clé de refresh : on l'incrémente après une soumission de quiz ou un équipement de skin.
  const [refreshKey, setRefreshKey] = useState(0);
  const refreshProfil = useCallback(() => setRefreshKey((k) => k + 1), []);

  const { data: profil, isLoading, isError } = useProfilXp(refreshKey, isEleve);

  // Récap de fin de quiz : on compare le profil précédent au nouveau. Si l'XP a augmenté
  // (donc une soumission vient de créditer), on pose un `recap` avec les deltas (XP, étoile,
  // flamme), le niveau, le level-up éventuel et les ratios de barre (avant→après).
  // Premier chargement (prev null) = baseline → pas de récap. Équiper un skin → Δxp 0 → pas de récap.
  const prevProfilRef = useRef(null);
  const [recap, setRecap] = useState(null);
  useEffect(() => {
    const n = profil;
    if (!n || n.xp_total == null) return;
    const prev = prevProfilRef.current;
    if (prev && n.xp_total > prev.xp_total) {
      const leveledUp = n.niveau > prev.niveau;
      setRecap({
        xpGagne: n.xp_total - prev.xp_total,
        etoilesGagnees: Math.max(0, (n.etoiles_total || 0) - (prev.etoiles_total || 0)),
        flammeGagnee: Math.max(0, (n.flamme_jours || 0) - (prev.flamme_jours || 0)),
        niveau: n.niveau,
        leveledUp,
        startRatio: leveledUp ? 0 : progressionRatio(prev),
        endRatio: progressionRatio(n),
      });
    }
    prevProfilRef.current = n;
  }, [profil]);
  const clearRecap = useCallback(() => setRecap(null), []);

  const [, doEquiper] = usePostApi(`${url}profils-xp/equiper-skin/`);

  // Équipe (ou retire avec skin_id='') un skin déjà débloqué, puis rafraîchit le profil.
  const equiperSkin = useCallback(async (type, skin_id) => {
    const res = await doEquiper({ type, skin_id });
    if (res?.success) refreshProfil();
    return res;
  }, [doEquiper, refreshProfil]);

  const value = useMemo(() => ({
    profil: profil || null,
    isLoading,
    isError,
    isEleve,
    refreshProfil,
    equiperSkin,
    recap,
    clearRecap,
  }), [profil, isLoading, isError, isEleve, refreshProfil, equiperSkin, recap, clearRecap]);

  return <XpContext.Provider value={value}>{children}</XpContext.Provider>;
};

// Hook d'accès. Tolérant si appelé hors provider (retourne un état neutre) → pas de crash.
export const useXp = () => useContext(XpContext) || {
  profil: null, isLoading: false, isError: false, isEleve: false,
  refreshProfil: () => {}, equiperSkin: async () => ({ success: false }),
  recap: null, clearRecap: () => {},
};

export default XpContext;
